import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import Modal from "@/components/base/Modal";
import Avatar from "@/components/base/Avatar";
import Lightbox from "@/components/base/Lightbox";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useApi } from "@/hooks/useApi";
import { apiPostForm } from "@/lib/api";
import type { BizPost } from "@/lib/types";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";
import { mediaUrl } from "@/lib/media";

function formatDate(value?: string): string {
  if (!value) return "";
  const str = String(value).replace(" ", "T");
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return value;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}.${m}.${day} ${hh}:${mm}`;
}

export default function PromoDetailPage() {
  const { no } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { requireLogin } = useAuth();

  const { data, loading, error, reload } = useApi<BizPost>(
    no ? `bizposts.php?no=${no}` : null
  );
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const post = data;
  const images = useMemo(() => post?.images ?? [], [post]);
  const w = post?.writer;
  const writerName =
    [w?.name, w?.aho].filter(Boolean).join(" ") ||
    [post?.name, post?.aho].filter(Boolean).join(" ");
  const bizName = post?.biz_name || w?.biz_name || "";
  const tel = String(post?.tel ?? "").trim();
  const showTel = (post?.show_tel ?? 1) !== 0;

  const handleDelete = async () => {
    if (!post) return;
    setDeleting(true);
    try {
      const form = new FormData();
      form.append("act", "delete");
      form.append("no", String(post.no));
      await apiPostForm("bizpost_write.php", form);
      toast("삭제되었습니다.", "success");
      navigate("/promo");
    } catch (e) {
      toast(e instanceof Error ? e.message : "삭제에 실패했습니다.", "error");
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  const handleEdit = () => {
    if (!requireLogin()) return;
    navigate(`/promo/write?no=${post?.no}`);
  };

  if (loading) {
    return (
      <>
        <PageBanner
          title="직업홍보"
          breadcrumb={[{ label: "직업홍보", to: "/promo" }, { label: "글 보기" }]}
        />
        <div className="mx-auto max-w-3xl px-4 md:px-6 py-10 space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-40 w-full" />
        </div>
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <PageBanner
          title="직업홍보"
          breadcrumb={[{ label: "직업홍보", to: "/promo" }, { label: "글 보기" }]}
        />
        <section className="mx-auto max-w-3xl px-4 md:px-6 py-12">
          <EmptyState
            icon="ri-error-warning-line"
            title="글을 불러오지 못했습니다"
            description={error ?? "존재하지 않는 글입니다."}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      </>
    );
  }

  return (
    <>
      <PageBanner
        title="직업홍보"
        breadcrumb={[{ label: "직업홍보", to: "/promo" }, { label: "글 보기" }]}
      />

      <article className="mx-auto max-w-3xl px-4 md:px-6 py-8 md:py-10">
        <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-7">
          {bizName && (
            <span className="inline-block rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
              {bizName}
            </span>
          )}
          <h1 className="mt-3 font-heading text-xl md:text-2xl font-bold text-foreground-950">
            {post.subject || "제목 없음"}
          </h1>

          <div className="mt-4 flex items-center gap-3">
            <Avatar name={writerName || "회원"} photo={w?.photo} size={40} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground-800">
                {writerName || "회원"}
              </p>
              <p className="text-xs text-foreground-400">
                {formatDate(post.regdate || post.reg_date)} · 조회 {post.hit ?? 0}
              </p>
            </div>
            {post.is_mine === 1 && (
              <div className="ml-auto flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleEdit}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-xs font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <i className="ri-edit-line mr-1" aria-hidden="true"></i>
                  수정
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmOpen(true)}
                  className="whitespace-nowrap rounded-full border border-accent-200 bg-accent-50 px-4 py-2 text-xs font-semibold text-accent-800 hover:bg-accent-100 transition-colors"
                >
                  <i className="ri-delete-bin-line mr-1" aria-hidden="true"></i>
                  삭제
                </button>
              </div>
            )}
          </div>

          {post.thumb && (
            <div className="mt-5 overflow-hidden rounded-lg bg-background-100">
              <img
                src={mediaUrl(post.thumb)}
                alt={post.subject}
                className="w-full object-cover object-top"
              />
            </div>
          )}

          <div className="mt-6 whitespace-pre-wrap text-sm md:text-base leading-relaxed text-foreground-800">
            {post.content}
          </div>

          {images.length > 0 && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {images.map((img, i) => (
                <button
                  key={`${img}-${i}`}
                  type="button"
                  onClick={() => setLightbox(i)}
                  className="group overflow-hidden rounded-lg bg-background-100"
                >
                  <div className="h-32 w-full overflow-hidden">
                    <img
                      src={mediaUrl(img)}
                      alt={`첨부 이미지 ${i + 1}`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </button>
              ))}
            </div>
          )}

          {showTel && tel && (
            <div className="mt-6 border-t border-background-200 pt-5">
              <a
                href={`tel:${tel.replace(/[^0-9+]/g, "")}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-phone-line" aria-hidden="true"></i>
                문의 전화 {tel}
              </a>
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/promo")}
            className="whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            목록으로
          </button>
        </div>
      </article>

      {lightbox !== null && (
        <Lightbox
          images={images}
          index={lightbox}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
          alt="첨부 이미지"
        />
      )}

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <div className="p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">
            글을 삭제할까요?
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            삭제한 글은 되돌릴 수 없습니다.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 whitespace-nowrap rounded-md bg-accent-600 px-5 py-3 text-sm font-bold text-background-50 hover:bg-accent-700 disabled:opacity-60 transition-colors"
            >
              {deleting ? "삭제 중…" : "삭제"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
            >
              취소
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}